import initShapeEngine, {
  clearCompiledShapeCacheWasm,
  clearShapeLeafHandlesWasm,
  clearLastFitStateWasm,
  compileShapeTreeWithLeafHandlesWasm,
  compileTextShapeLeafWasm,
  fontMetricsRegisteredWasm,
  registerFontWasm,
  resolveQualityEpsilonForPixelErrorWasm,
} from 'fluveria-engine-wasm';
import type { ArcBounds, ArcRenderPrep, PreprocessedArcData } from './arcSdf.js';

export const BUNDLED_FONT_IDS = ['rubik', 'inter', 'pacifico', 'great-vibes', 'noto-sans-sc'] as const;
export type BundledFontId = typeof BUNDLED_FONT_IDS[number];
export const SDF_FONT_LOADED_EVENT = 'sdf-font-loaded';

interface BundledFontOption {
  label: string;
  value: BundledFontId;
}

interface FontMetrics {
  unitsPerEm: number;
  ascender: number;
  descender: number;
  xHeight: number;
  capHeight: number;
  lineGap: number;
}

export type TextWhiteSpace = 'normal' | 'nowrap' | 'pre' | 'pre-wrap';
export type TextHorizontalAlign = 'start' | 'center' | 'end';
export type TextVerticalAlign = TextHorizontalAlign;
export type SvgFillRule = 'nonzero' | 'evenodd';

interface TextLayoutOptions {
  autoWidth: boolean;
  width: number;
  autoHeight: boolean;
  height: number;
  fontSizePx: number;
  whiteSpace: TextWhiteSpace;
  horizontalAlign: TextHorizontalAlign;
  verticalAlign: TextVerticalAlign;
  lineHeight: number;
}

interface CachedArcData {
  arcs: Float32Array;
  contourOffsets: Uint32Array;
  prep: PreprocessedArcData;
  renderPrep: ArcRenderPrep;
  gpuBufferData: ArcGpuBufferData;
  cpuSamplerData: ArcCpuSamplerData;
}

interface ArcQueryParts extends CachedArcData {
  bounds: ArcBounds;
}

interface ShapeLeafHandleData {
  handle: WasmShapeLeafHandleLike;
  handleId: number;
  key: string;
  layoutBounds: ArcBounds;
  contourBounds: ArcBounds;
  epsilon: number;
}

interface TextShapeLeafData extends ShapeLeafHandleData {
  fontMetrics: FontMetrics;
}

interface WasmArcQuerySourceLike {
  arcs(): Float32Array;
  contourOffsets(): Uint32Array;
  bounds(): Float32Array;
  distArcs(): Float32Array;
  distCount(): number;
  windSegs(): Float32Array;
  windCount(): number;
  bvhNodeData(): Float32Array;
  bvhLeafIndices(): Uint32Array;
  bvhNodeCount(): number;
  windRowOffsets(): Uint32Array;
  windRowIndices(): Uint32Array;
  windRightRowIndices(): Uint32Array;
  windRightRowSums(): Float32Array;
  yMin(): number;
  rowSize(): number;
  rowCount(): number;
  windMaxSpan(): number;
  gpuBufferData(slotIndex: number): WasmArcGpuBufferDataLike;
}

interface WasmRetainedArcQueryLike extends WasmArcQuerySourceLike {
  distanceAt(x: number, y: number): number;
  coverageAtWithDistance(x: number, y: number, pixelSize: number, distanceToBoundary: number): number;
  byteLength(): number;
  free(): void;
}

interface WasmCompiledShapeLike extends WasmRetainedArcQueryLike {
  key(): string;
  layoutBounds(): Float32Array;
  contourBounds(): Float32Array;
  hasQuery(): boolean;
  leafCount(): number;
}

interface WasmShapeLeafHandleLike {
  id(): number;
  key(): string;
  layoutBounds(): Float32Array;
  contourBounds(): Float32Array;
  byteLength(): number;
  release(): void;
  free(): void;
}

interface WasmArcGpuBufferDataLike {
  meta(): Uint8Array;
  windRowIndices(): Uint32Array;
  free(): void;
}

interface WasmArcCpuSamplerDataLike {
  distanceAt(x: number, y: number): number;
  coverageAt(x: number, y: number, pixelSize: number, distanceToBoundary: number): number;
  byteLength(): number;
  free(): void;
}

export interface ShapeArcQueryData extends ArcQueryParts {
  key: string;
}

interface ShapeTreeBuildResult {
  query: ShapeArcQueryData | null;
  layoutBounds: ArcBounds;
  contourBounds: ArcBounds;
  key: string;
}

export interface ArcWindRowGrid {
  originY: number;
  rowSize: number;
}

export type ArcAffineTransform = readonly [number, number, number, number, number, number];

type ShapeTreeLeafKind = 'raw' | 'rect' | 'circle' | 'handle';

export interface ShapeTreeLeafInput {
  kind: ShapeTreeLeafKind;
  fillRule?: SvgFillRule;
  arcs?: Float32Array;
  contourOffsets?: Uint32Array;
  layoutBounds?: ArcBounds;
  transform?: ArcAffineTransform;
  key?: string;
  params?: readonly [number, number, number];
  handleId?: number;
}

export interface ShapeTreeBuildInput {
  leaves: readonly ShapeTreeLeafInput[];
  treeOps: Uint32Array;
  treeTransforms: Float32Array;
  treeTransformKeys: readonly string[];
  windRowGrid?: ArcWindRowGrid;
}

export interface ArcGpuBufferData {
  meta: Uint8Array;
  distArcs: Float32Array;
  bvhNodes: Float32Array;
  bvhLeaves: Uint32Array;
  windSegs: Float32Array;
  windRowOffsets: Uint32Array;
  windRowIndices: Uint32Array;
}

interface ArcCpuSamplerData extends WasmArcCpuSamplerDataLike {}

interface SizedCacheEntry<T> {
  value: T;
  bytes: number;
}

let initPromise: Promise<void> | null = null;
let ready = false;
const bundledFontHandles = new Map<BundledFontId, number>();
const bundledFontMetrics = new Map<BundledFontId, FontMetrics>();
const bundledFontPromises = new Map<BundledFontId, Promise<void>>();

const DEFAULT_FONT_ID: BundledFontId = 'rubik';
const DEFAULT_UNITS_PER_EM = 1000;
const SHAPE_TARGET_PX = 0.5;
const TEXT_EPSILON_LIMITS = { min: 0.5, max: 32 };
const DEFAULT_TEXT_WIDTH = 400;
const DEFAULT_TEXT_HEIGHT = 120;
const DEFAULT_TEXT_LINE_HEIGHT = 1.2;
const DEFAULT_TEXT_WHITE_SPACE: TextWhiteSpace = 'pre';
const DEFAULT_TEXT_HORIZONTAL_ALIGN: TextHorizontalAlign = 'center';
const DEFAULT_TEXT_VERTICAL_ALIGN: TextVerticalAlign = 'center';
const DEFAULT_SVG_FILL_RULE: SvgFillRule = 'nonzero';
const MEGABYTE = 1024 * 1024;

/**
 * Cache policy budgets only. They bound continuous-value drags such as text
 * spacing and size; they are not geometric tolerances.
 */
const TEXT_LEAF_CACHE_MAX_BYTES = 64 * MEGABYTE;
const SHAPE_QUERY_CACHE_MAX_BYTES = 64 * MEGABYTE;
const ARC_CACHE_ENTRY_OVERHEAD_BYTES = 256;
const FONT_SOURCES: Record<BundledFontId, string> = {
  rubik: 'fonts/rubik-regular.ttf',
  inter: 'fonts/inter-regular.ttf',
  pacifico: 'fonts/pacifico-regular.ttf',
  'great-vibes': 'fonts/great-vibes-regular.ttf',
  'noto-sans-sc': 'fonts/noto-sans-sc-regular.otf',
};

const SHAPE_TREE_LEAF_KIND: Record<ShapeTreeLeafKind, number> = {
  raw: 0,
  rect: 1,
  circle: 2,
  handle: 3,
};
const TEXT_ENCODER = new TextEncoder();

const arcCpuSamplerFinalizer = typeof FinalizationRegistry === 'undefined'
  ? null
  : new FinalizationRegistry<WasmArcCpuSamplerDataLike>((sampler) => {
      sampler.free();
    });

const BUNDLED_FONT_LABELS: Record<BundledFontId, string> = {
  rubik: 'Rubik',
  inter: 'Inter',
  pacifico: 'Pacifico',
  'great-vibes': 'Great Vibes',
  'noto-sans-sc': 'Noto Sans SC',
};

export const BUNDLED_FONT_OPTIONS: readonly BundledFontOption[] = BUNDLED_FONT_IDS.map(value => ({
  label: BUNDLED_FONT_LABELS[value],
  value,
}));

export async function ensureShapeEngineInit(): Promise<void> {
  if (!initPromise) initPromise = doInit();
  return initPromise;
}

async function ensureBundledFontLoaded(fontId: BundledFontId): Promise<void> {
  await ensureShapeEngineInit();
  if (bundledFontHandles.has(fontId)) return;
  const existing = bundledFontPromises.get(fontId);
  if (existing) return existing;
  const promise = loadBundledFont(fontId).finally(() => {
    bundledFontPromises.delete(fontId);
  });
  bundledFontPromises.set(fontId, promise);
  return promise;
}

export async function ensureBundledFontsLoaded(fontIds: readonly BundledFontId[]): Promise<void> {
  await Promise.all(fontIds.map(fontId => ensureBundledFontLoaded(fontId)));
}

async function doInit(): Promise<void> {
  await initShapeEngine();
  ready = true;
}

async function loadBundledFont(fontId: BundledFontId): Promise<void> {
  if (bundledFontHandles.has(fontId)) return;
  const base = ((import.meta as unknown as Record<string, Record<string, string>>).env?.BASE_URL) ?? '/';
  let response: Response;
  try {
    response = await fetch(`${base}${FONT_SOURCES[fontId]}`);
  } catch {
    // Network failure (or no fetch target outside the browser): the font
    // simply stays unloaded, same as a non-ok response.
    return;
  }
  if (!response.ok) return;
  const bytes = new Uint8Array(await response.arrayBuffer());
  const fontHandle = registerFontWasm(bytes);
  bundledFontHandles.set(fontId, fontHandle);
  bundledFontMetrics.set(fontId, parseMetrics(fontMetricsRegisteredWasm(fontHandle)));
  notifyBundledFontLoaded(fontId);
}

function notifyBundledFontLoaded(fontId: BundledFontId): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(SDF_FONT_LOADED_EVENT, { detail: { fontId } }));
}

export function getDefaultBundledFontId(): BundledFontId {
  return DEFAULT_FONT_ID;
}

export function resolveBundledFontId(value: unknown): BundledFontId {
  return typeof value === 'string' && (BUNDLED_FONT_IDS as readonly string[]).includes(value) ? value as BundledFontId : DEFAULT_FONT_ID;
}

export function fontHeightUnits(fontId: BundledFontId): number {
  const metrics = bundledFontMetrics.get(fontId);
  return Math.max((metrics?.ascender ?? DEFAULT_UNITS_PER_EM) - (metrics?.descender ?? 0), 1);
}

export function autoTextEpsilon(fontId: BundledFontId, fontSizePx: number): number {
  const unitsPerEm = bundledFontMetrics.get(fontId)?.unitsPerEm ?? DEFAULT_UNITS_PER_EM;
  if (ready) {
    return resolveQualityEpsilonForPixelErrorWasm(unitsPerEm, Math.max(fontSizePx, 1), SHAPE_TARGET_PX);
  }
  return clamp(SHAPE_TARGET_PX * unitsPerEm / Math.max(fontSizePx, 1), TEXT_EPSILON_LIMITS.min, TEXT_EPSILON_LIMITS.max);
}

export function resolveTextWhiteSpace(value: unknown): TextWhiteSpace {
  return value === 'normal' || value === 'nowrap' || value === 'pre-wrap' ? value : DEFAULT_TEXT_WHITE_SPACE;
}

export function resolveTextHorizontalAlign(value: unknown): TextHorizontalAlign {
  return value === 'start' || value === 'end' ? value : DEFAULT_TEXT_HORIZONTAL_ALIGN;
}

export function resolveTextVerticalAlign(value: unknown): TextVerticalAlign {
  return value === 'start' || value === 'end' ? value : DEFAULT_TEXT_VERTICAL_ALIGN;
}

export function resolveSvgFillRule(value: unknown): SvgFillRule {
  return value === 'evenodd' ? 'evenodd' : DEFAULT_SVG_FILL_RULE;
}

export function normalizeTextLayoutOptions(input: Partial<TextLayoutOptions> = {}): TextLayoutOptions {
  return {
    autoWidth: input.autoWidth ?? true,
    width: finiteOr(input.width, DEFAULT_TEXT_WIDTH),
    autoHeight: input.autoHeight ?? true,
    height: finiteOr(input.height, DEFAULT_TEXT_HEIGHT),
    fontSizePx: finiteOr(input.fontSizePx, 24),
    whiteSpace: resolveTextWhiteSpace(input.whiteSpace),
    horizontalAlign: resolveTextHorizontalAlign(input.horizontalAlign),
    verticalAlign: resolveTextVerticalAlign(input.verticalAlign),
    lineHeight: Math.max(finiteOr(input.lineHeight, DEFAULT_TEXT_LINE_HEIGHT), 0.1),
  };
}

function textLayoutKey(options: TextLayoutOptions): string {
  const widthKey = options.autoWidth ? 'auto' : roundKey(options.width);
  const heightKey = options.autoHeight ? 'auto' : roundKey(options.height);
  const sizeKey = options.autoWidth && options.autoHeight ? 'auto' : roundKey(options.fontSizePx);
  return `aw:${options.autoWidth ? 1 : 0}:w:${widthKey}:ah:${options.autoHeight ? 1 : 0}:h:${heightKey}:ws:${options.whiteSpace}:ha:${options.horizontalAlign}:va:${options.verticalAlign}:lh:${roundKey(options.lineHeight)}:sz:${sizeKey}`;
}

export function getOrBuildTextShapeLeaf(text: string, fontId: BundledFontId, letterSpacing: number, epsilon: number, layoutInput: Partial<TextLayoutOptions> = {}): TextShapeLeafData | null {
  if (!ready || !text) return null;
  const fontHandle = bundledFontHandles.get(fontId);
  const metrics = bundledFontMetrics.get(fontId);
  if (!fontHandle || !metrics) {
    void ensureBundledFontLoaded(fontId);
    return null;
  }
  const layout = normalizeTextLayoutOptions(layoutInput);
  const spacingUnits = letterSpacing * metrics.unitsPerEm;
  const key = `text:${fontId}:${spacingUnits}:${epsilon}:${textLayoutKey(layout)}:${text}`;
  const cached = textCache.get(key);
  if (cached) return cached;

  let handle: WasmShapeLeafHandleLike;
  try {
    handle = compileTextShapeLeafWasm(
      fontHandle,
      text,
      spacingUnits,
      epsilon,
      layout.autoWidth,
      layout.width,
      layout.autoHeight,
      layout.height,
      layout.fontSizePx,
      layout.whiteSpace,
      layout.horizontalAlign,
      layout.verticalAlign,
      layout.lineHeight,
    ) as unknown as WasmShapeLeafHandleLike;
  } catch {
    return null;
  }
  const data = shapeLeafDataFromHandle(handle, key, epsilon);
  if (!data) {
    disposeShapeLeafHandle(handle);
    return null;
  }
  const textLeaf: TextShapeLeafData = { ...data, fontMetrics: metrics };
  textCache.set(key, textLeaf);
  return textLeaf;
}

export interface EngineFontInfo {
  handle: number;
  unitsPerEm: number;
  ascender: number;
  descender: number;
}

/**
 * Cache-key component for anything caching shape-compile output: loaded
 * fonts change the compile result without any graph/param change.
 */
export function getLoadedFontRegistryKey(): string {
  return [...bundledFontHandles.entries()]
    .map(([fontId, handle]) => `${fontId}:${handle}`)
    .sort()
    .join(',');
}

/** Currently loaded bundled fonts, for the engine-side shape compiler. */
export function getLoadedFontRegistry(): Record<string, EngineFontInfo> {
  const registry: Record<string, EngineFontInfo> = {};
  for (const [fontId, handle] of bundledFontHandles) {
    const metrics = bundledFontMetrics.get(fontId);
    if (!metrics) continue;
    registry[fontId] = {
      handle,
      unitsPerEm: metrics.unitsPerEm,
      ascender: metrics.ascender,
      descender: metrics.descender,
    };
  }
  return registry;
}

interface WasmShapeGraphCompileResultLike {
  key(): string;
  layoutBounds(): Float32Array;
  contourBounds(): Float32Array;
  error(): string | undefined;
  hasQuery(): boolean;
  takeCompiled(): WasmCompiledShapeLike | undefined;
}

/**
 * Wrap an engine-side shape graph compile result the same way
 * `buildArcQueryFromShapeTree` wraps a compiled tree: query cache by key,
 * retained CPU sampler, bounds extraction.
 */
export function adoptCompiledShapeGraph(result: WasmShapeGraphCompileResultLike): ShapeTreeBuildResult {
  const key = result.key();
  const layoutBounds = boundsFromArray(result.layoutBounds());
  const contourBounds = boundsFromArray(result.contourBounds());
  const compiled = result.takeCompiled();
  if (!result.hasQuery() || !compiled) {
    compiled?.free();
    return { query: null, layoutBounds, contourBounds, key };
  }
  const cached = shapeQueryCache.get(key);
  if (cached) {
    compiled.free();
    return { query: cached, layoutBounds, contourBounds: cached.bounds, key };
  }
  const queryParts = readRetainedArcQueryData(compiled);
  if (!queryParts) {
    compiled.free();
    return { query: null, layoutBounds, contourBounds, key };
  }
  const data: ShapeArcQueryData = { ...queryParts, key };
  registerArcCpuSampler(data);
  shapeQueryCache.set(key, data);
  return { query: data, layoutBounds, contourBounds: data.bounds, key };
}

export function clearArcCaches(): void {
  textCache.clear();
  shapeQueryCache.clear();
  if (ready) {
    clearLastFitStateWasm();
    clearCompiledShapeCacheWasm();
    clearShapeLeafHandlesWasm();
  }
}

function shapeLeafDataFromHandle(handle: WasmShapeLeafHandleLike, fallbackKey: string, epsilon: number): ShapeLeafHandleData | null {
  const handleId = handle.id();
  const layoutBoundsValues = handle.layoutBounds();
  const contourBoundsValues = handle.contourBounds();
  if (handleId === 0 || layoutBoundsValues.length < 4 || contourBoundsValues.length < 4) return null;
  return {
    handle,
    handleId,
    key: handle.key() || fallbackKey,
    layoutBounds: boundsFromArray(layoutBoundsValues),
    contourBounds: boundsFromArray(contourBoundsValues),
    epsilon,
  };
}

function disposeShapeLeafData(data: ShapeLeafHandleData): void {
  disposeShapeLeafHandle(data.handle);
}

function disposeShapeLeafHandle(handle: WasmShapeLeafHandleLike): void {
  try {
    handle.release();
  } finally {
    handle.free();
  }
}

function parseMetrics(values: Float32Array): FontMetrics {
  const unitsPerEm = values[0] || 1000;
  return {
    unitsPerEm,
    ascender: Math.abs(Number(values[1] ?? 0)),
    descender: -Math.abs(Number(values[2] ?? 0)),
    xHeight: Number(values[3] ?? 0),
    capHeight: Number(values[4] ?? 0),
    lineGap: Number(values[5] ?? 0),
  };
}

function clamp(value: number, min: number, max: number): number {
  if (!Number.isFinite(value)) return min;
  return Math.min(max, Math.max(min, value));
}

function finiteOr(value: unknown, fallback: number): number {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function roundKey(value: number): string {
  return Number.isFinite(value) ? value.toFixed(4) : 'nan';
}

export function buildArcQueryFromShapeTree(input: ShapeTreeBuildInput): ShapeTreeBuildResult | null {
  if (!ready) return null;
  const serialized = serializeShapeTreeInput(input);
  const compiled = compileShapeTreeWithLeafHandlesWasm(
    serialized.rawArcs,
    serialized.rawContourOffsets,
    serialized.rawLeafContourOffsets,
    serialized.leafKinds,
    serialized.leafFillRules,
    serialized.leafParams,
    serialized.leafLayoutBounds,
    serialized.leafTransforms,
    serialized.leafKeyOffsets,
    serialized.leafKeyBytes,
    serialized.leafHandles,
    input.treeOps,
    input.treeTransforms,
    serialized.treeTransformKeyOffsets,
    serialized.treeTransformKeyBytes,
    isValidWindRowGrid(input.windRowGrid),
    input.windRowGrid?.originY ?? 0,
    input.windRowGrid?.rowSize ?? 1,
  ) as unknown as WasmCompiledShapeLike;
  const key = compiled.key();
  const layoutBounds = boundsFromArray(compiled.layoutBounds());
  const compiledContourBounds = boundsFromArray(compiled.contourBounds());
  if (!compiled.hasQuery()) {
    compiled.free();
    return { query: null, layoutBounds, contourBounds: compiledContourBounds, key };
  }

  const cached = shapeQueryCache.get(key);
  if (cached) {
    compiled.free();
    return { query: cached, layoutBounds, contourBounds: cached.bounds, key };
  }

  const queryParts = readRetainedArcQueryData(compiled);
  if (!queryParts) {
    compiled.free();
    return { query: null, layoutBounds, contourBounds: compiledContourBounds, key };
  }
  const data: ShapeArcQueryData = { ...queryParts, key };
  registerArcCpuSampler(data);
  shapeQueryCache.set(key, data);
  return { query: data, layoutBounds, contourBounds: data.bounds, key };
}

function isValidWindRowGrid(grid: ArcWindRowGrid | undefined): grid is ArcWindRowGrid {
  return grid !== undefined
    && Number.isFinite(grid.originY)
    && Number.isFinite(grid.rowSize)
    && Math.abs(grid.rowSize) > 0;
}

function normalizeArcAffineTransform(transform: ArcAffineTransform | undefined): ArcAffineTransform {
  if (!transform || transform.length < 6) return [1, 0, 0, 1, 0, 0];
  const a = Number(transform[0]);
  const b = Number(transform[1]);
  const c = Number(transform[2]);
  const d = Number(transform[3]);
  const tx = Number(transform[4]);
  const ty = Number(transform[5]);
  return [
    Number.isFinite(a) ? a : 1,
    Number.isFinite(b) ? b : 0,
    Number.isFinite(c) ? c : 0,
    Number.isFinite(d) ? d : 1,
    Number.isFinite(tx) ? tx : 0,
    Number.isFinite(ty) ? ty : 0,
  ];
}

function serializeShapeTreeInput(input: ShapeTreeBuildInput): {
  rawArcs: Float32Array;
  rawContourOffsets: Uint32Array;
  rawLeafContourOffsets: Uint32Array;
  leafKinds: Uint8Array;
  leafFillRules: Uint8Array;
  leafParams: Float32Array;
  leafLayoutBounds: Float32Array;
  leafTransforms: Float32Array;
  leafKeyOffsets: Uint32Array;
  leafKeyBytes: Uint8Array;
  leafHandles: Uint32Array;
  treeTransformKeyOffsets: Uint32Array;
  treeTransformKeyBytes: Uint8Array;
} {
  const rawArcs: number[] = [];
  const rawContourOffsets: number[] = [0];
  const rawLeafContourOffsets: number[] = [0];
  const leafKinds = new Uint8Array(input.leaves.length);
  const leafFillRules = new Uint8Array(input.leaves.length);
  const leafParams = new Float32Array(input.leaves.length * 3);
  const leafLayoutBounds = new Float32Array(input.leaves.length * 4);
  const leafTransforms = new Float32Array(input.leaves.length * 6);
  const leafHandles = new Uint32Array(input.leaves.length);
  const leafKeys: string[] = [];

  input.leaves.forEach((leaf, index) => {
    leafKinds[index] = SHAPE_TREE_LEAF_KIND[leaf.kind];
    leafFillRules[index] = resolveSvgFillRule(leaf.fillRule) === 'evenodd' ? 1 : 0;
    const params = leaf.params ?? [0, 0, 0];
    leafParams[index * 3] = finiteOr(params[0], 0);
    leafParams[index * 3 + 1] = finiteOr(params[1], 0);
    leafParams[index * 3 + 2] = finiteOr(params[2], 0);
    writeBounds(leafLayoutBounds, index * 4, leaf.layoutBounds ?? { minX: 0, minY: 0, maxX: 0, maxY: 0 });
    leafTransforms.set(normalizeArcAffineTransform(leaf.transform), index * 6);
    leafHandles[index] = leaf.kind === 'handle' ? Math.max(0, Math.trunc(finiteOr(leaf.handleId, 0))) : 0;
    leafKeys.push(leaf.key ?? '');

    if (leaf.kind === 'raw' && leaf.arcs && leaf.arcs.length > 0) {
      const arcBase = rawArcs.length / 8;
      for (const value of leaf.arcs) rawArcs.push(value);
      const offsets = normalizeContourOffsets(leaf.contourOffsets ?? new Uint32Array([0, leaf.arcs.length / 8]), leaf.arcs.length / 8);
      for (const offset of offsets.slice(1)) {
        rawContourOffsets.push(arcBase + offset);
      }
    }
    rawLeafContourOffsets.push(rawContourOffsets.length - 1);
  });

  const leafKeyTable = encodeStringTable(leafKeys);
  const transformKeyTable = encodeStringTable(input.treeTransformKeys);
  return {
    rawArcs: new Float32Array(rawArcs),
    rawContourOffsets: new Uint32Array(rawContourOffsets),
    rawLeafContourOffsets: new Uint32Array(rawLeafContourOffsets),
    leafKinds,
    leafFillRules,
    leafParams,
    leafLayoutBounds,
    leafTransforms,
    leafKeyOffsets: leafKeyTable.offsets,
    leafKeyBytes: leafKeyTable.bytes,
    leafHandles,
    treeTransformKeyOffsets: transformKeyTable.offsets,
    treeTransformKeyBytes: transformKeyTable.bytes,
  };
}

function writeBounds(target: Float32Array, offset: number, bounds: ArcBounds): void {
  target[offset] = finiteOr(bounds.minX, 0);
  target[offset + 1] = finiteOr(bounds.minY, 0);
  target[offset + 2] = finiteOr(bounds.maxX, 0);
  target[offset + 3] = finiteOr(bounds.maxY, 0);
}

function encodeStringTable(values: readonly string[]): { offsets: Uint32Array; bytes: Uint8Array } {
  const offsets = new Uint32Array(values.length + 1);
  const chunks: Uint8Array[] = [];
  let total = 0;
  values.forEach((value, index) => {
    offsets[index] = total;
    const bytes = TEXT_ENCODER.encode(value);
    chunks.push(bytes);
    total += bytes.length;
  });
  offsets[values.length] = total;
  const out = new Uint8Array(total);
  let cursor = 0;
  for (const chunk of chunks) {
    out.set(chunk, cursor);
    cursor += chunk.length;
  }
  return { offsets, bytes: out };
}

function readRetainedArcQueryData(query: WasmRetainedArcQueryLike): ArcQueryParts | null {
  const queryParts = readArcQueryParts(query);
  if (!queryParts) return null;
  return {
    ...queryParts,
    cpuSamplerData: retainedQueryCpuSampler(query),
  };
}

function readArcQueryParts(query: WasmArcQuerySourceLike): Omit<ArcQueryParts, 'cpuSamplerData'> | null {
  const boundsValues = query.bounds();
  if (boundsValues.length < 4 || query.distCount() === 0) return null;
  const distArcs = query.distArcs();
  const windSegs = query.windSegs();
  const prep: PreprocessedArcData = {
    distArcs,
    distCount: query.distCount(),
    windSegs,
    windCount: query.windCount(),
  };
  const renderPrep: ArcRenderPrep = {
    prep,
    bvh: {
      nodeData: query.bvhNodeData(),
      leafIndices: query.bvhLeafIndices(),
      nodeCount: query.bvhNodeCount(),
    },
    bins: {
      yMin: query.yMin(),
      rowSize: query.rowSize(),
      rowCount: query.rowCount(),
      windMaxSpan: query.windMaxSpan(),
      windRowOffsets: query.windRowOffsets(),
      windRowIndices: query.windRowIndices(),
      windRightRowIndices: query.windRightRowIndices(),
      windRightRowSums: query.windRightRowSums(),
    },
  };
  return {
    arcs: query.arcs(),
    contourOffsets: query.contourOffsets(),
    prep,
    renderPrep,
    gpuBufferData: readArcGpuBufferData(query.gpuBufferData(0), renderPrep),
    bounds: boundsFromArray(boundsValues),
  };
}

function retainedQueryCpuSampler(query: WasmRetainedArcQueryLike): ArcCpuSamplerData {
  return {
    distanceAt: (x, y) => query.distanceAt(x, y),
    coverageAt: (x, y, pixelSize, distanceToBoundary) => query.coverageAtWithDistance(x, y, pixelSize, distanceToBoundary),
    byteLength: () => query.byteLength(),
    free: () => query.free(),
  };
}

function registerArcCpuSampler(owner: CachedArcData): void {
  arcCpuSamplerFinalizer?.register(owner, owner.cpuSamplerData, owner.cpuSamplerData);
}

function readArcGpuBufferData(gpu: WasmArcGpuBufferDataLike, renderPrep: ArcRenderPrep): ArcGpuBufferData {
  try {
    return {
      meta: gpu.meta(),
      distArcs: paddedFloat32(renderPrep.prep.distArcs, 12),
      bvhNodes: paddedFloat32(renderPrep.bvh.nodeData, 8),
      bvhLeaves: paddedUint32(renderPrep.bvh.leafIndices, 1),
      windSegs: paddedFloat32(renderPrep.prep.windSegs, 8),
      windRowOffsets: paddedUint32(renderPrep.bins.windRowOffsets, 2),
      windRowIndices: gpu.windRowIndices(),
    };
  } finally {
    gpu.free();
  }
}

function paddedFloat32(values: Float32Array, minLength: number): Float32Array {
  return values.length >= minLength ? values : new Float32Array(minLength);
}

function paddedUint32(values: Uint32Array, minLength: number): Uint32Array {
  return values.length >= minLength ? values : new Uint32Array(minLength);
}

function normalizeContourOffsets(offsets: Uint32Array, arcCount: number): number[] {
  if (arcCount <= 0) return [0];
  if (offsets.length < 2) return [0, arcCount];
  const out: number[] = [];
  let previous = 0;
  for (let i = 0; i < offsets.length; i++) {
    const value = Math.min(Math.max(0, offsets[i] ?? 0), arcCount);
    if (i === 0 || value >= previous) {
      out.push(value);
      previous = value;
    }
  }
  if (out[0] !== 0) out.unshift(0);
  if (out[out.length - 1] !== arcCount) out.push(arcCount);
  return Array.from(new Set(out));
}

function boundsFromArray(values: Float32Array): ArcBounds {
  return {
    minX: values[0],
    minY: values[1],
    maxX: values[2],
    maxY: values[3],
  };
}

class BoundedCache<T> {
  private entries = new Map<string, SizedCacheEntry<T>>();
  private totalBytes = 0;

  constructor(
    private readonly maxBytes: number,
    private readonly measure: (key: string, value: T) => number,
    private readonly dispose?: (value: T) => void,
  ) {}

  get(key: string): T | undefined {
    const entry = this.entries.get(key);
    if (!entry) return undefined;
    this.entries.delete(key);
    this.entries.set(key, entry);
    return entry.value;
  }

  set(key: string, value: T): void {
    const previous = this.entries.get(key);
    if (previous) {
      this.entries.delete(key);
      this.totalBytes -= previous.bytes;
      this.dispose?.(previous.value);
    }
    const bytes = Math.max(1, this.measure(key, value));
    this.entries.set(key, { value, bytes });
    this.totalBytes += bytes;
    this.evictOldest();
  }

  clear(): void {
    if (this.dispose) {
      for (const entry of this.entries.values()) this.dispose(entry.value);
    }
    this.entries.clear();
    this.totalBytes = 0;
  }

  private evictOldest(): void {
    while (this.totalBytes > this.maxBytes && this.entries.size > 1) {
      const oldestKey = this.entries.keys().next().value;
      if (oldestKey === undefined) break;
      const oldest = this.entries.get(oldestKey);
      if (!oldest) break;
      this.entries.delete(oldestKey);
      this.totalBytes -= oldest.bytes;
      this.dispose?.(oldest.value);
    }
  }
}

const textCache = new BoundedCache<TextShapeLeafData>(TEXT_LEAF_CACHE_MAX_BYTES, shapeLeafCacheEntryByteLength, disposeShapeLeafData);
// Shape result cache keyed by the Rust-normalized shape key. A hit returns
// the materialized JS arrays directly, skipping both the WASM resolve and the
// leaf marshalling, so material-only param changes do not rebuild geometry.
const shapeQueryCache = new BoundedCache<ShapeArcQueryData>(SHAPE_QUERY_CACHE_MAX_BYTES, arcCacheEntryByteLength);

function arcCacheEntryByteLength(key: string, value: CachedArcData): number {
  return ARC_CACHE_ENTRY_OVERHEAD_BYTES
    + key.length * 2
    + value.arcs.byteLength
    + value.contourOffsets.byteLength
    + value.prep.distArcs.byteLength
    + value.prep.windSegs.byteLength
    + value.renderPrep.bvh.nodeData.byteLength
    + value.renderPrep.bvh.leafIndices.byteLength
    + value.renderPrep.bins.windRowOffsets.byteLength
    + value.renderPrep.bins.windRowIndices.byteLength
    + value.renderPrep.bins.windRightRowIndices.byteLength
    + value.renderPrep.bins.windRightRowSums.byteLength
    + value.cpuSamplerData.byteLength();
}

function shapeLeafCacheEntryByteLength(key: string, value: ShapeLeafHandleData): number {
  return ARC_CACHE_ENTRY_OVERHEAD_BYTES
    + key.length * 2
    + value.key.length * 2
    + value.handle.byteLength();
}
